import { useMutation, useQuery } from "@tanstack/react-query";
import { trainingGroupApiService } from "modules/Library/TrainingGroup/api/traininggroup.api";
import { TrainingGroupRecord } from "modules/Library/TrainingGroup/helper/traininggroup.types";
import { CurrentUser, GetUserData } from "common/service/CommonService";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

// ------------------------------------------------------------------
//-- TRAINING GROUP
// ------------------------------------------------------------------ 

export const useTrainingGroupService = ({
  searchText,
  trainingtypeid,
  trainingcategoryid,
  pageNumber = 1,
  pageSize = 10,
}: {
  searchText?: string | null;
  trainingtypeid?: string | number | null;
  trainingcategoryid?: string | number | null;
  pageNumber?: number;
  pageSize?: number;
} = {}) => {
  const SelectAllQuery = useQuery({
    queryKey: ["trainingGroup-selectAll", searchText, trainingtypeid, trainingcategoryid, pageNumber, pageSize],
    queryFn: () => trainingGroupApiService.getSelectAllData(searchText, trainingtypeid, trainingcategoryid, pageNumber, pageSize),
  });

  const InsertMutation = useMutation<ApiResponse, Error, TrainingGroupRecord>({
    mutationFn: (payload: TrainingGroupRecord) =>
      trainingGroupApiService.insertData(payload),
  });

  const UpdateMutation = useMutation<ApiResponse, Error, TrainingGroupRecord>({
    mutationFn: (payload: TrainingGroupRecord) =>
      trainingGroupApiService.updateData(payload),
  });

  const DeleteMutation = useMutation<ApiResponse, Error, { id: number | null; updatereason: string }>({
    mutationFn: (payload: { id: number | null; updatereason: string }) =>
      trainingGroupApiService.deleteData(payload),
  });

  return {
    SelectAllQuery,
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
  };
};

export const useTrainingGroupAssignService = ({
  trainingType = null,
  trainingCategory = null,
  trainingTitle = null,
  searchText,
  pageNumber,
  pageSize,
  trainingGroupId = null,
  activeTab = "all",
  connectionStatus,
}: {
  trainingType?: null | number | string;
  trainingCategory?: null | number | string;
  trainingTitle?: null | number | string;
  searchText?: string;
  pageNumber?: number;
  pageSize?: number;
  trainingGroupId?: number | null;
  activeTab?: string;
  connectionStatus?: string;
} = {}) => {
  const isConnected = connectionStatus === undefined || connectionStatus === "connected";

  /* =========================
     TRAINING TYPE DROP DOWN
  ========================= */
  const TrainingTypeQuery = useQuery({
    queryKey: ["group-training-type-dropdown"],
    queryFn: () => trainingGroupApiService.trainingTypeDropdown({}),
    enabled: isConnected,
  });

  /* =========================
     CATEGORY DROP DOWN
  ========================= */
  const CategoryQuery = useQuery({
    queryKey: ["group-training-category-dropdown", trainingType],
    queryFn: () =>
      trainingGroupApiService.categoryDropdown({
        TypeId: Number(trainingType),
      }),
    enabled: !!trainingType && isConnected,
  });

  /* =========================
     TITLE DROP DOWN
  ========================= */
  const TitleQuery = useQuery({
    queryKey: ["group-training-title-dropdown", trainingCategory],
    queryFn: () =>
      trainingGroupApiService.titleDropdown({
        TitleCategoryId: Number(trainingCategory),
      }),
    enabled: !!trainingCategory && isConnected,
  });

  /* =========================
     SELECT ALL (AVAILABLE TITLES)
  ========================= */
  const SelectAllQuery = useQuery({
    queryKey: [
      "group-training-topics-select-all",
      { trainingType, trainingCategory, trainingTitle, searchText, pageNumber, pageSize, activeTab },
    ],
    queryFn: () =>
      trainingGroupApiService.titleSelectAll({
        TypeId:trainingType === "" ? null : trainingType,
        TitleCategoryId: trainingCategory === "" ? null :trainingCategory ,
        TitleId: trainingTitle === "" ? null : trainingTitle,
        SearchText: searchText || null,
        PageNumber: pageNumber,
        PageSize: pageSize,
      }),
    enabled: activeTab === "all" && isConnected,
  });

  /* =========================
     INSERT MUTATION
  ========================= */
  const InsertMutation = useMutation({
    mutationFn: (payload: any) => trainingGroupApiService.groupTrainingInsert(payload),
  });

  /* =========================
     ASSIGNED SELECT ALL
  ========================= */
  const AssignedSelectAllQuery = useQuery({
    queryKey: [
      "group-assigned-training-select-all",
      {
        trainingGroupId,
        trainingType,
        trainingCategory,
        trainingTitle,
        searchText,
        pageNumber,
        pageSize,
      },
    ],
    queryFn: () => {
      return trainingGroupApiService.groupTrainingSelectAll({
        traininggroupid: Number(trainingGroupId),
        trainingtypeid:
          trainingType && trainingType !== "" ? Number(trainingType) : null,
        trainingcategoryid:
          trainingCategory && trainingCategory !== ""
            ? Number(trainingCategory)
            : null,
        trainingtitleid:
          trainingTitle && trainingTitle !== "" ? Number(trainingTitle) : null,
        searchtext: searchText && searchText !== "" ? searchText : null,
        currentpage: pageNumber,
        pagesize: pageSize,
        status: "A",
      });
    },
    enabled: !!trainingGroupId && activeTab === "assigned",
  });

  /* =========================
     ASSIGNED FULL LIST (For Filters)
  ========================= */


  /* =========================
     DELETE MUTATION
  ========================= */
  const DeleteMutation = useMutation({
    mutationFn: (payload: { id: number | null; updatereason: string }) => {
      const user = GetUserData() as CurrentUser;
      const deletePayload = {
        traininggroupdetailid: payload.id,
        updatereason: payload.updatereason,
        updatedby: user.userid,
      };
      return trainingGroupApiService.groupTrainingDelete(deletePayload);
    },
  });

  return {
    TrainingTypeQuery,
    CategoryQuery,
    TitleQuery,
    SelectAllQuery,
    InsertMutation,
    AssignedSelectAllQuery,
    DeleteMutation,
  };
};
